// NodeCurrencyField.jsx

import React, { useCallback, useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  CURRENCY_CODE_OPTIONS,
  DEFAULT_CURRENCY_CODE,
  getCurrencyCodeLabel,
  normalizeCurrencyCodeFieldValue,
} from 'utilities/Currency';

// CSS
import './NodeCurrencyField.css';

// Components
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

/**
 * NodeCurrencyField
 *
 * ISO 4217 currency-code picker for schema-driven fields (e.g. Project Currency).
 * Stores an uppercase code string (USD). Distinct from Money (monetary amounts).
 *
 * @param {*} value - Current field value (ISO code).
 * @param {Function} onChange - Callback when a code is selected.
 * @param {boolean} [readOnly=false]
 * @param {boolean} [previewMode=false]
 *
 * @returns {ReactNode}
 */
const NodeCurrencyField = ({
  value,
  onChange,
  readOnly = false,
  previewMode = false,
}) => {
  const { theme } = useTheme();
  const isReadOnly = Boolean(readOnly || previewMode);

  const selected = useMemo(() => {
    const normalized = normalizeCurrencyCodeFieldValue(value);
    return normalized || '';
  }, [value]);

  const options = useMemo(() => {
    if (!selected) return CURRENCY_CODE_OPTIONS;
    if (CURRENCY_CODE_OPTIONS.some((option) => option.value === selected)) {
      return CURRENCY_CODE_OPTIONS;
    }
    return [{ value: selected, label: selected }, ...CURRENCY_CODE_OPTIONS];
  }, [selected]);

  const handleChange = useCallback((event) => {
    if (isReadOnly || typeof onChange !== 'function') return;
    const next = normalizeCurrencyCodeFieldValue(event.target.value);
    onChange(next);
  }, [isReadOnly, onChange]);

  if (isReadOnly) {
    return (
      <div className="node-currency-field-container">
        <TextInput
          value={selected ? getCurrencyCodeLabel(selected) : ''}
          placeholder=""
          startIconKey="payments"
          startIconColor={theme.foregroundMuted}
          iconSize={22}
          hybrid
          readOnly
        />
      </div>
    );
  }

  return (
    <div className="node-currency-field-container">
      <SelectInput
        value={selected || DEFAULT_CURRENCY_CODE}
        options={options}
        startIconKey="payments"
        iconSize={22}
        hybrid
        onChange={handleChange}
      />
    </div>
  );
};

NodeCurrencyField.displayName = 'NodeCurrencyField';

export default NodeCurrencyField;
